import React from 'react';
import {observer, inject} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {SimpleTabs, ITab} from '@techsee/techsee-ui-common/lib/tabs/simple';
import {Datepicker} from '@techsee/techsee-ui-common/lib/datepicker';
import {TsVideoPlayerPopup} from '../../components/ts-video-player-popup';
import {HistorySearchByTags} from '../../components/search-by-tags/component';
import {HistoryController} from './HistoryController';
import Translate from '../../components/translate/Translate';
import './History.scss';

export interface IHistoryProps {
    historyController?: HistoryController;
}

@inject('historyController')
@observer
export class History extends TechseeBaseComponent<IHistoryProps> {
    private startDateWrapperRef = React.createRef<HTMLDivElement>();
    private endDateWrapperRef = React.createRef<HTMLDivElement>();
    private getValidRecords = <T extends {roomId?: {_id?: string} | null}>(
        records: T[],
        hasContent?: (record: T) => boolean
    ): T[] =>
        records.filter((record) => {
            if (!record?.roomId?._id) {
                return false;
            }

            return hasContent ? hasContent(record) : true;
        });

    componentDidMount(): void {
        document.addEventListener('click', this.handleOutsideClick);
    }

    componentWillUnmount(): void {
        document.removeEventListener('click', this.handleOutsideClick);
    }

    private handleDatePickerButtonClick = (
        e: React.MouseEvent<HTMLButtonElement>,
        wrapperRef: React.RefObject<HTMLDivElement>,
        isOpen: boolean,
        setOpen: (isOpen: boolean) => void
    ): void => {
        e.preventDefault();
        e.stopPropagation();
        const input = wrapperRef.current?.querySelector('input') as HTMLInputElement;
        if (input) {
            if (isOpen) {
                input.blur();
                setOpen(false);
            } else {
                input.focus();
                input.click();
                setOpen(true);
            }
        }
    };

    private handleOutsideClick = (e: MouseEvent): void => {
        const ctrl = this.props.historyController;
        if (!ctrl) return;

        const target = e.target as HTMLElement;

        if (!target.closest('.react-datepicker-wrapper') && !target.closest('.input-group-btn')) {
            ctrl.resetDatePickerOpenStates();
        }
    };

    renderInternal(): JSX.Element {
        const {historyController} = this.props;

        if (!historyController) {
            return <div>Loading...</div>;
        }

        const ctrl = historyController;
        const state = ctrl.state;
        const translate = ctrl.translate;
        const useNewInterface = ctrl.useNewInterface;
        const dir = ctrl.dir;
        const onBackToInvite = ctrl.onBackToInvite;
        const searchByTags = ctrl.getSearchByTags();

        if (searchByTags) {
            return this.renderSearchByTags();
        }

        return (
            <div className='ts-invite-history ts-wizard-content'>
                {state.videoLink && (
                    <TsVideoPlayerPopup
                        isScreenMode={true}
                        videoLink={state.videoLink}
                        onCloseVideo={ctrl.closeVideo}
                    />
                )}

                <div className='ts-history-top-section text-center'>
                    <button
                        type='button'
                        className={`pull-left btn-wizard btn-wizard-back ${useNewInterface ? 'btn btn-wizard btn-wizard-back' : ''}`}
                        onClick={onBackToInvite}>
                        <Translate text='HISTORY.VIEW.EXIT' />
                    </button>

                    <h3 className='ts-history-title orange-text'>
                        {state.customerId && (
                            <span>
                                <Translate text='HISTORY.VIEW.BY_ID' /> {state.customerId}
                            </span>
                        )}
                        {state.customerNumber && (
                            <span>
                                <Translate text='HISTORY.VIEW.BY_NUMBER' /> {state.customerNumber}
                            </span>
                        )}
                    </h3>
                </div>

                <div className='ts-invite-history-container'>
                    <div className='ts-invite-history-sidebar'>
                        <h4>
                            <Translate text='HISTORY.VIEW.FILTER_OPTIONS' />
                        </h4>
                        <br />
                        <div>
                            <h5>
                                <Translate text='HISTORY.VIEW.START_DATE' />
                            </h5>
                            <div className='input-group'>
                                <div
                                    ref={this.startDateWrapperRef}
                                    onClick={() => {
                                        ctrl.setStartDatePickerOpen(true);
                                    }}>
                                    <Datepicker
                                        startDate={state.startDate || undefined}
                                        onDateChange={(date: Date) => {
                                            ctrl.setStartDate(date);
                                        }}
                                    />
                                </div>
                                <span className='input-group-btn'>
                                    <button
                                        type='button'
                                        className='btn btn-default'
                                        onClick={(e) =>
                                            this.handleDatePickerButtonClick(
                                                e,
                                                this.startDateWrapperRef,
                                                state.startDatePickerOpen,
                                                ctrl.setStartDatePickerOpen
                                            )
                                        }>
                                        <i className='glyphicon glyphicon-calendar' />
                                    </button>
                                </span>
                            </div>
                        </div>
                        <div>
                            <h5>
                                <Translate text='HISTORY.VIEW.END_DATE' />
                            </h5>
                            <div className='input-group'>
                                <div
                                    ref={this.endDateWrapperRef}
                                    onClick={() => {
                                        ctrl.setEndDatePickerOpen(true);
                                    }}>
                                    <Datepicker
                                        startDate={state.endDate || undefined}
                                        onDateChange={(date: Date) => {
                                            ctrl.setEndDate(date);
                                        }}
                                    />
                                </div>
                                <span className='input-group-btn'>
                                    <button
                                        type='button'
                                        className='btn btn-default'
                                        onClick={(e) =>
                                            this.handleDatePickerButtonClick(
                                                e,
                                                this.endDateWrapperRef,
                                                state.endDatePickerOpen,
                                                ctrl.setEndDatePickerOpen
                                            )
                                        }>
                                        <i className='glyphicon glyphicon-calendar' />
                                    </button>
                                </span>
                            </div>
                        </div>
                        {this.renderRecordingTypeFilter()}
                        {ctrl.searchTags && this.renderSearchTagsForm()}
                    </div>
                    <div className='ts-invite-history-main'>{this.renderTabs()}</div>
                </div>
            </div>
        );
    }

    private renderRecordingTypeFilter(): JSX.Element {
        const ctrl = this.props.historyController!;
        const options = ctrl.getRecordingTypeFilterOptions();

        return (
            <div className='ts-history-recording-type-filter'>
                <h5>
                    <Translate text='HISTORY.VIEW.RECORDING_TYPE' />
                </h5>
                {options.map((option) => {
                    const checked = ctrl.isMediaTypeSelected(option.value);
                    return (
                        <label key={option.value} className='recording-type-option'>
                            <input
                                type='checkbox'
                                checked={checked}
                                onChange={() => ctrl.toggleMediaTypeFilter(option.value)}
                            />
                            <span>
                                <Translate text={option.labelKey} />
                            </span>
                        </label>
                    );
                })}
            </div>
        );
    }

    private renderSearchTagsForm(): JSX.Element {
        const ctrl = this.props.historyController!;
        const state = ctrl.state;
        const translate = ctrl.translate;

        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    ctrl.searchForTags(state.searchTagForm);
                }}
                className='history-search-tags-container'
                noValidate>
                <h5>
                    <Translate text='HISTORY.VIEW.SEARCH_BY_TAG' />
                </h5>
                <div className={`btn-group form-group ${state.emptySearchTermSubmitted ? 'empty-submit' : ''}`}>
                    <input
                        type='text'
                        name='search-tag-input'
                        placeholder={translate('HISTORY.VIEW.ENTER_A_TAG')}
                        value={state.searchTagForm.searchTerm}
                        onChange={(e) => {
                            ctrl.setSearchTerm(e.target.value);
                        }}
                        pattern={ctrl.allLanguagesNoHtml.source}
                        maxLength={140}
                        required
                        className='btn tag-search form-control'
                    />
                    <button type='submit' className='btn btn-default'>
                        <span className='glyphicon glyphicon-search' />
                    </button>
                </div>
                <div className='form-group text-center'>
                    <button type='button' className='btn btn-default btn-sm' onClick={() => ctrl.clearSearchForm()}>
                        <Translate text='HISTORY.VIEW.CLEAR_SEARCH' />
                    </button>
                </div>
            </form>
        );
    }

    private renderTabs(): JSX.Element {
        const ctrl = this.props.historyController!;
        const state = ctrl.state;
        const translate = ctrl.translate;

        const tabsList: ITab[] = [
            {
                title: `${translate('HISTORY.VIEW.ALL')} (${state.filteredData.records.length})`,
                content: () => this.renderAllTab()
            },
            {
                title: `${translate('HISTORY.VIEW.IMAGES')} (${state.filteredData.images})`,
                content: () => this.renderImagesTab()
            },
            {
                title: `${translate('HISTORY.VIEW.VIDEOS')} (${state.filteredData.videos})`,
                content: () => this.renderVideosTab()
            },
            {
                title: `${translate('HISTORY.VIEW.MESSAGES')} (${state.filteredData.messages})`,
                content: () => this.renderMessagesTab()
            },
            {
                title: `${translate('HISTORY.VIEW.FILES')} (${state.filteredData.files})`,
                content: () => this.renderFilesTab()
            },
            {
                title: `${translate('HISTORY.VIEW.RECORDINGS')} (${state.filteredData.recordings})`,
                content: () => this.renderRecordingsTab()
            }
        ];

        return <SimpleTabs uid='history-tabs' tabsList={tabsList} />;
    }

    private renderAllTab(): JSX.Element {
        const ctrl = this.props.historyController!;
        const state = ctrl.state;
        const translate = ctrl.translate;

        const sortedRecords = [...this.getValidRecords(state.filteredData.records)].sort(
            (a, b) => new Date(b.timestamp).valueOf() - new Date(a.timestamp).valueOf()
        );

        return (
            <ul className='list-group'>
                {sortedRecords.map((record) => (
                    <div key={record.roomId._id}>
                        <div
                            className='list-group-date btn btn-block text-left'
                            onClick={() => ctrl.loadRoom(record.roomId, record.images, record.videos)}>
                            {ctrl.displayDates(record.timestamp)}
                            <small className='lower-case pull-right'>
                                {record.images.length > 0 && (
                                    <span>
                                        {record.images.length} <Translate text='HISTORY.VIEW.IMAGES' />
                                    </span>
                                )}
                                {record.videos.length > 0 && (
                                    <span>
                                        {record.videos.length} <Translate text='HISTORY.VIEW.VIDEOS' />
                                    </span>
                                )}
                                {record.messages.length > 0 && (
                                    <span>
                                        {record.messages.length} <Translate text='HISTORY.VIEW.MESSAGES' />
                                    </span>
                                )}
                                {record.files.length > 0 && (
                                    <span>
                                        {record.files.length} <Translate text='HISTORY.VIEW.FILES' />
                                    </span>
                                )}
                                {record.recordings && record.recordings.length > 0 && (
                                    <span>
                                        {record.recordings.length} <Translate text='HISTORY.VIEW.RECORDINGS' />
                                    </span>
                                )}
                            </small>
                            {ctrl.roomIsActiveOffline(record.roomId) && (
                                <>
                                    <span className='offline-dot' />
                                    <small className='lower-case'>
                                        (<Translate text='HISTORY.VIEW.ACTIVE' />)
                                    </small>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </ul>
        );
    }

    private renderImagesTab(): JSX.Element {
        const ctrl = this.props.historyController!;
        const state = ctrl.state;
        const translate = ctrl.translate;
        const validRecords = this.getValidRecords(state.filteredData.records, (record) => record.images?.length > 0);

        const showNoResults =
            ctrl.searchTags &&
            state.searchTagForm.submitted &&
            state.searchTagForm.searchTerm &&
            state.filteredData.images === 0;

        return (
            <>
                {showNoResults && (
                    <div className='text-center'>
                        <Translate
                            text='HISTORY.VIEW.NO_TAG_SEARCH_RESULTS'
                            translateValues={{searchTerm: state.searchTagForm.searchTerm}}
                        />
                    </div>
                )}
                {validRecords.map((record) => (
                    <div key={record.roomId._id} className='ts-history-images-list'>
                        {record.images.length > 0 && (
                            <div className='ts-history-images-date'>
                                <small>{ctrl.displayDates(record.timestamp)}</small>
                                {ctrl.roomIsActiveOffline(record.roomId) && <span className='offline-dot' />}
                            </div>
                        )}
                        {record.images.map((image) => (
                            <div
                                key={image._id || `${record.roomId._id}-image-${image.url}`}
                                className={`media-container thumbnail-container ${image.storageType === 'TEMP' ? 'temp' : ''}`}>
                                <div
                                    className='thumbnail'
                                    onClick={() => {
                                        if (image.storageType !== 'TEMP') {
                                            ctrl.loadDashboard(image._id, null, record.roomId);
                                        }
                                    }}>
                                    <img src={image.url} alt='' />
                                    {image.revisions && image.revisions.length > 0 && (
                                        <div className='last-edit-caption caption'>
                                            <Translate text='HISTORY.VIEW.LAST_EDITED' />{' '}
                                            {ctrl.displayEditDates(image.timestamp)}
                                        </div>
                                    )}
                                    {image.storageType === 'TEMP' && (
                                        <div className='caption temp'>
                                            <p className='temp-caption'>
                                                <Translate text='HISTORY.VIEW.TEMP_IMAGE' />
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </>
        );
    }

    private renderVideosTab(): JSX.Element {
        const ctrl = this.props.historyController!;
        const state = ctrl.state;
        const translate = ctrl.translate;

        const showNoResults =
            ctrl.searchTags &&
            state.searchTagForm.submitted &&
            state.searchTagForm.searchTerm &&
            state.filteredData.videos === 0;

        const sortedRecords = [
            ...this.getValidRecords(state.filteredData.records, (record) => record.videos?.length > 0)
        ].sort((a, b) => new Date(b.timestamp).valueOf() - new Date(a.timestamp).valueOf());

        return (
            <>
                {showNoResults && (
                    <div className='text-center'>
                        <Translate
                            text='HISTORY.VIEW.NO_TAG_SEARCH_RESULTS'
                            translateValues={{searchTerm: state.searchTagForm.searchTerm}}
                        />
                    </div>
                )}
                {sortedRecords.map((record) => (
                    <div key={record.roomId._id} className='ts-history-images-list'>
                        {record.videos.length > 0 && (
                            <div className='ts-history-images-date'>
                                {ctrl.roomIsActiveOffline(record.roomId) && <span className='offline-dot' />}
                            </div>
                        )}
                        {record.videos.map((video) => (
                            <div
                                key={video._id || `${record.roomId._id}-video-${video.url || video.link}`}
                                className='media-container'>
                                <span>{ctrl.displayDates(video.recStartTime || video.timestamp)}</span>
                                {!video.storageType ? (
                                    <video className='recordings' controls>
                                        <source src={video.link} type='video/mp4' />
                                    </video>
                                ) : (
                                    <div
                                        className={`thumbnail-container ${video.storageType === 'TEMP' ? 'temp' : ''}`}>
                                        <div
                                            className='thumbnail'
                                            onClick={() => {
                                                if (video.storageType !== 'TEMP') {
                                                    ctrl.loadDashboard(null, video._id, record.roomId);
                                                }
                                            }}>
                                            <img src={video.url} alt='' />
                                            {video.revisions && video.revisions.length > 0 && (
                                                <div className='last-edit-caption caption'>
                                                    <Translate text='HISTORY.VIEW.LAST_EDITED' />{' '}
                                                    {ctrl.displayEditDates(video.timestamp)}
                                                </div>
                                            )}
                                            {video.storageType === 'TEMP' && (
                                                <div className='caption temp'>
                                                    <p className='temp-caption'>
                                                        <Translate text='HISTORY.VIEW.TEMP_VIDEO' />
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </>
        );
    }

    private renderMessagesTab(): JSX.Element {
        const ctrl = this.props.historyController!;
        const state = ctrl.state;
        const validRecords = this.getValidRecords(state.filteredData.records, (record) => record.messages?.length > 0);

        return (
            <ul className='list-group'>
                {validRecords.map((record) => (
                    <div key={record.roomId._id}>
                        {record.messages.length > 0 && (
                            <div className='list-group-date'>
                                <small>{ctrl.displayDates(record.timestamp)}</small>
                                {ctrl.roomIsActiveOffline(record.roomId) && <span className='offline-dot' />}
                            </div>
                        )}
                        {record.messages.map((message, messageIndex) => (
                            <li
                                key={`${record.roomId._id}-message-${messageIndex}-${message.substring(0, 20)}`}
                                className='list-group-item'
                                dangerouslySetInnerHTML={{__html: message}}
                            />
                        ))}
                    </div>
                ))}
            </ul>
        );
    }

    private renderFilesTab(): JSX.Element {
        const ctrl = this.props.historyController!;
        const state = ctrl.state;
        const validRecords = this.getValidRecords(state.filteredData.records, (record) => record.files?.length > 0);

        return (
            <ul className='list-group'>
                {validRecords.map((record) => (
                    <div key={record.roomId._id}>
                        {record.files.length > 0 && (
                            <div className='list-group-date'>
                                <small>{ctrl.displayDates(record.timestamp)}</small>
                                {ctrl.roomIsActiveOffline(record.roomId) && <span className='offline-dot' />}
                            </div>
                        )}
                        {record.files.map((file) => {
                            const basename = file.split('/').pop() || file;

                            return (
                                <li key={`${record.roomId._id}-file-${file}`} className='list-group-item'>
                                    <a href={file} target='_blank' rel='noopener noreferrer'>
                                        {basename}
                                    </a>
                                </li>
                            );
                        })}
                    </div>
                ))}
            </ul>
        );
    }

    private renderRecordingsTab(): JSX.Element {
        const ctrl = this.props.historyController!;
        const state = ctrl.state;
        const translate = ctrl.translate;

        return (
            <table className='recording-table'>
                <thead>
                    <tr>
                        <th>
                            <Translate text='HISTORY.VIEW.RECORDINGS.SESSION_ID' />
                        </th>
                        <th>
                            <Translate text='HISTORY.VIEW.RECORDINGS.AGENT_NAME' />
                        </th>
                        <th>
                            <Translate text='HISTORY.VIEW.RECORDINGS.SESSION_START_TIME' />
                        </th>
                        <th>
                            <Translate text='HISTORY.VIEW.RECORDINGS.SESSION_DURATION' />
                        </th>
                        <th>
                            <Translate text='HISTORY.VIEW.RECORDINGS.RECORDING_START_TIME' />
                        </th>
                        <th>
                            <Translate text='HISTORY.VIEW.RECORDINGS.RECORDING_DURATION' />
                        </th>
                        <th>
                            <Translate text='HISTORY.VIEW.RECORDINGS.RECORDING_TYPE' />
                        </th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {state.recordings.map((recording) => (
                        <tr key={recording.sessionId}>
                            <th>{recording.sessionId}</th>
                            <th>{`${recording.firstName} ${recording.lastName}`}</th>
                            <th>{ctrl.displayRecordingHistoryDates(recording.sessionStartTime)}</th>
                            <th>
                                {ctrl.calculateAndFormatDuration(
                                    recording.sessionStartTime,
                                    ctrl.sessionEndOrRecFallback(recording)
                                )}
                            </th>
                            <th>{ctrl.displayRecordingHistoryDates(recording.recStartTime)}</th>
                            <th>{ctrl.calculateAndFormatDuration(recording.recStartTime, recording.recEndTime)}</th>
                            <th>
                                <Translate text={ctrl.getRecordingTypeLabelKey(recording.mediaType)} />
                            </th>
                            <th>
                                <span className='view' onClick={() => ctrl.setCurrentVideoToPlay(recording.link)}>
                                    <Translate text='HISTORY.VIEW.RECORDINGS.VIEW' /> ››
                                </span>
                            </th>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    private renderSearchByTags(): JSX.Element {
        const ctrl = this.props.historyController!;
        const state = ctrl.state;
        // Create controller object that HistorySearchByTags expects
        const searchByTagsController = {
            searchByTagsInput: '',
            startDate: state.startDate,
            endDate: state.endDate,
            filteredData: state.filteredData,
            searchByTagsCount: state.searchByTagsCount,
            executeSearchByTags: (tags: string, fromDate: string, toDate: string, page: number) =>
                ctrl.executeSearchByTags(tags, fromDate, toDate, page),
            getSearchByTagsCount: (tags: string, fromDate: string, toDate: string) =>
                ctrl.getSearchByTagsCount(tags, fromDate, toDate),
            loadRoom: (roomRecord: any, images: any[], videos: any[]) => ctrl.loadRoom(roomRecord, images, videos),
            loadDashboard: (imageId: string | null, videoId: string | null, roomRecord: any) =>
                ctrl.loadDashboard(imageId, videoId, roomRecord),
            translate: ctrl.translate
        };

        return <HistorySearchByTags controller={searchByTagsController} />;
    }
}
